//Global imports
import React, { useState } from 'react';
import {
    CAccordion,
    CAccordionBody,
    CAccordionHeader,
    CAccordionItem,
    CFormInput,
    CFormTextarea,
} from '@coreui/react';
import JoditEditor from 'jodit-react';

//File imports
import {
    DocumentDetailPageErrors,
    DocumentDetailPageInfoData,
    Errors,
    FormData,
    StringUtils,
} from '../../Types/types';
import { Language } from '../../../../language/English';
import CTABox from '../../../../assets/images/cta_box.webp';
import HowTo from '../../../../assets/images/how_it_works.webp';
import DocumentPageBannerHeadingHint from '../../../../assets/images/document_page_banner_heading.webp';
import DocumentPageHighlightHint from '../../../../assets/images/document_page_highlight.webp';
import DocumentPageBenefit from '../../../../assets/images/document_page_benefit.webp';
import DocumentPageShortDescription from '../../../../assets/images/document_page_short_description.webp';
import DocumentPageDescription from '../../../../assets/images/document_page_description.webp';
import DocumentPageYoutubeLink from '../../../../assets/images/document_page_youtubelink.webp';

//Component imports
import { FAQs, SeoFields } from '../../Components';
import {
    FieldHeadingWithHintText,
    FieldPreviewModal,
    HintText,
    LengthChecker,
} from '../../../../components';
import { DOCUMENT_DETAIL_PAGE_INFO } from '../../../../Constant';

interface DocumentDetailPageProps {
    onBlurUrlField: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onChageDescriptionText: (newContent: string) => void;
    setLoader: (status: boolean) => void;
    setUniqueFolderName: (name: string) => void;
    setShowFaqNote: (status: boolean) => void;
    handleHightLightChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => void;
    handleBenefitChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: 'title' | 'description',
        index: number,
    ) => void;

    data: DocumentDetailPageInfoData;
    errors: DocumentDetailPageErrors;
    stringUtils: StringUtils;
    name: string;
    editorConfig: any;
    editor: React.RefObject<any>;
    folderName: string | null;
    showFaqNote: boolean;
}

const DocumentDetailPage: React.FC<DocumentDetailPageProps> = ({
    onBlurUrlField,
    handleChange,
    handlePaste,
    onKeyDown,
    onChageDescriptionText,
    setLoader,
    setUniqueFolderName,
    setShowFaqNote,
    handleHightLightChange,
    handleBenefitChange,

    data,
    errors,
    stringUtils,
    name,
    editorConfig,
    editor,
    folderName,
    showFaqNote,
}) => {
    const [fieldPreviewModalVisible, setFieldPreviewModalVisible] =
        useState(false);
    const [hintImage, setHintImage] = useState<string>('');

    const openHintModal = (imageSource: string) => {
        setHintImage(imageSource);
        setFieldPreviewModalVisible(true);
    };
    return (
        <>
            {/* Url, Google Meta Title, Meta Keyword, Meta Description Fields */}
            <SeoFields
                name={name}
                stringUtils={stringUtils}
                data={{
                    url: data?.url,
                    metaTitle: data?.metaTitle,
                    metaKeyword: data?.metaKeyword,
                    metaDescription: data?.metaDescription,
                }}
                errors={{
                    url: errors?.url,
                    metaTitle: errors?.metaTitle,
                    metaKeyword: errors?.metaKeyword,
                    metaDescription: errors?.metaDescription,
                }}
                onBlurUrlField={onBlurUrlField}
                handleChange={handleChange}
                handlePaste={handlePaste}
                onKeyDown={onKeyDown}
            />

            {/* Banner heading */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.BANNER_HEADING}
                openModal={() => {
                    openHintModal(DocumentPageBannerHeadingHint);
                }}
            />
            <CFormInput
                value={data?.bannerHeading || ''}
                invalid={!!errors?.bannerHeading}
                feedback={errors?.bannerHeading}
                className="mt-2"
                type="text"
                name={stringUtils.convertToCamelCase(Language.BANNER_HEADING)}
                placeholder={Language.BANNER_HEADING}
                onChange={handleChange}
                maxLength={60}
            />
            <LengthChecker
                currentLength={data?.bannerHeading?.length || 0}
                maxLength={60}
            />

            {/* Short Description */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.SHORT_DESCRIPTION}
                openModal={() => {
                    openHintModal(DocumentPageShortDescription);
                }}
            />
            <HintText marginBottom="10px">
                {Language.SHORT_DESCRIPTION_HINT}
            </HintText>
            <CFormTextarea
                placeholder={Language.ENTER_SHORT_DESCRIPTION}
                onKeyDown={onKeyDown}
                onChange={handleChange}
                onPaste={handlePaste}
                value={data?.shortDescription}
                invalid={!!errors?.shortDescription}
                feedback={errors?.shortDescription}
                maxLength={1000}
                style={{ height: 180 }}
                name={stringUtils.convertToCamelCase(
                    Language.SHORT_DESCRIPTION,
                )}
            />
            <LengthChecker
                currentLength={data?.shortDescription?.length ?? 0}
                maxLength={1000}
            />

            {/* Document Highlights */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DOCUMENT_HIGHLIGHTS}
                openModal={() => {
                    openHintModal(DocumentPageHighlightHint);
                }}
            />
            <CAccordion alwaysOpen className="mt-2">
                {data?.highlights &&
                    Array.isArray(data?.highlights) &&
                    data?.highlights.map((item, index) => (
                        <CAccordionItem key={index} itemKey={index}>
                            <CAccordionHeader className="fw-semibold">
                                {Language.HIGHLIGHT} {index + 1}
                                {item.title && (
                                    <span className="ms-2 text-muted">
                                        — {item.title}
                                    </span>
                                )}
                            </CAccordionHeader>
                            <CAccordionBody>
                                <CFormInput
                                    value={item.title}
                                    onChange={(e) => {
                                        handleHightLightChange(
                                            e,
                                            'title',
                                            index,
                                        );
                                    }}
                                    placeholder={Language.TITLE}
                                    maxLength={21}
                                />
                                <LengthChecker
                                    currentLength={item.title.length}
                                    maxLength={21}
                                />

                                <CFormInput
                                    className="mt-2"
                                    value={item.description}
                                    onChange={(e) => {
                                        handleHightLightChange(
                                            e,
                                            'description',
                                            index,
                                        );
                                    }}
                                    name={stringUtils.convertToCamelCase(
                                        Language.DOCUMENT_HIGHLIGHTS,
                                    )}
                                    placeholder={Language.DESCRIPTION}
                                    maxLength={70}
                                />
                                <LengthChecker
                                    currentLength={item.description.length}
                                    maxLength={70}
                                />
                            </CAccordionBody>
                        </CAccordionItem>
                    ))}
            </CAccordion>
            {errors?.highlights && (
                <div className="alert alert-danger">{errors?.highlights}</div>
            )}

            {/* Document Benefits */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DOCUMENT_BENEFITS}
                openModal={() => {
                    openHintModal(DocumentPageBenefit);
                }}
            />
            <CAccordion alwaysOpen className="mt-2">
                {data?.benefits &&
                    Array.isArray(data?.benefits) &&
                    data?.benefits.map((item, index) => (
                        <CAccordionItem key={index} itemKey={index}>
                            <CAccordionHeader className="fw-semibold">
                                {Language.BENEFIT} {index + 1}
                                {item.title && (
                                    <span className="ms-2 text-muted">
                                        — {item.title}
                                    </span>
                                )}
                            </CAccordionHeader>
                            <CAccordionBody>
                                <CFormInput
                                    value={item.title}
                                    onChange={(e) => {
                                        handleBenefitChange(e, 'title', index);
                                    }}
                                    placeholder={Language.TITLE}
                                    maxLength={24}
                                />
                                <LengthChecker
                                    currentLength={item.title.length}
                                    maxLength={24}
                                />

                                <CFormInput
                                    className="mt-2"
                                    value={item.description}
                                    onChange={(e) => {
                                        handleBenefitChange(
                                            e,
                                            'description',
                                            index,
                                        );
                                    }}
                                    name={stringUtils.convertToCamelCase(
                                        Language.DOCUMENT_BENEFITS,
                                    )}
                                    placeholder={Language.DESCRIPTION}
                                    maxLength={60}
                                />

                                <LengthChecker
                                    currentLength={item.description.length}
                                    maxLength={60}
                                />
                            </CAccordionBody>
                        </CAccordionItem>
                    ))}
            </CAccordion>
            {errors?.benefits && (
                <div className="alert alert-danger">{errors?.benefits}</div>
            )}

            {/* Description */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.DESCRIPTION}
                openModal={() => {
                    openHintModal(DocumentPageDescription);
                }}
            />
            <HintText marginBottom="10px">{Language.DESCRIPTION_HINT}</HintText>

            <div className="mb-3 p-3 rounded border bg-light">
                <h6>
                    <strong>
                        {Language.HOWTO_USE_DESCRIPTION_PLACEHOLDER}:
                    </strong>
                </h6>
                <ul>
                    <li>
                        <code>{Language.CTA_BANNER}</code>
                        {Language.CTA_BANNER_HINT}
                    </li>
                    <li>
                        <code>{Language.DOCUMENT_STEPS}</code>
                        {Language.DOCUMENT_STEPS_HINT}
                    </li>
                </ul>
                <p>{Language.PLACE_PLACEHOLDERS}</p>

                <div className="row mt-3">
                    <div className="col-md-6 mb-2">
                        <h6 className="mb-2">
                            Placeholder: <code>{Language.CTA_BANNER}</code>
                        </h6>
                        <img
                            src={CTABox}
                            style={{
                                objectFit: 'contain',
                            }}
                            alt="CTA Banner Example"
                            className="img-fluid rounded border"
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <h6 className="mb-2">
                            Placeholder: <code> {Language.DOCUMENT_STEPS}</code>
                        </h6>
                        <img
                            src={HowTo}
                            style={{
                                objectFit: 'contain',
                            }}
                            alt="Document Steps Example"
                            className="img-fluid rounded border"
                        />
                    </div>
                </div>
            </div>

            <div id="description">
                <JoditEditor
                    ref={editor}
                    value={data?.description}
                    config={editorConfig}
                    onBlur={(newContent) => onChageDescriptionText(newContent)}
                />
            </div>
            {errors?.description && (
                <div className="alert alert-danger">{errors?.description}</div>
            )}

            {/* YouTube Link */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.YOUTUBE_LINK}
                openModal={() => {
                    openHintModal(DocumentPageYoutubeLink);
                }}
            />
            <HintText marginBottom="10px">{Language.YOUTUBE_HINT}</HintText>
            <CFormInput
                type="text"
                placeholder={Language.ENTER_YOUTUBE_VIDEO_LINK}
                onChange={handleChange}
                value={data?.youtubeLink}
                invalid={!!errors?.youtubeLink}
                feedback={errors?.youtubeLink}
                name={stringUtils.convertToCamelCase(Language.YOUTUBE_LINK)}
            />

            {/* FAQs Section */}
            <FAQs
                folderName={folderName}
                faqValidationError={errors?.faqs}
                setLoader={(status: boolean) => setLoader(status)}
                setUniqueFolderName={(name: string) =>
                    setUniqueFolderName(name)
                }
                showFaqNote={showFaqNote}
                setShowFaqNote={(status: boolean) => setShowFaqNote(status)}
            />

            <FieldPreviewModal
                visible={fieldPreviewModalVisible}
                onClose={() => setFieldPreviewModalVisible(false)}
                imageSrc={hintImage}
            />
        </>
    );
};

export default DocumentDetailPage;
